/**
 * The one definition of "no network" for the offline layer.
 *
 * A network failure is the only condition under which a cached copy may be
 * served in place of the server's answer. HTTP errors and aborts are not
 * network failures: the server answered, or the caller gave up.
 */

import { RequestTimeoutError } from '@shared/services/requestTimeout';

/** Thrown when a request cannot be served: offline and nothing cached. */
export class OfflineError extends Error {
  /** The request url that could not be served. */
  readonly url: string;

  /** Build the error for a url that has no cached fallback. */
  constructor(url: string) {
    super(`Offline and no cached copy for ${url}`);
    this.name = 'OfflineError';
    this.url = url;
  }
}

/**
 * True for the failures `fetch` raises when the network is unreachable.
 *
 * `fetch` rejects with a `TypeError` for DNS, connection and CORS failures;
 * the shared client turns its own deadline into `RequestTimeoutError`.
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof RequestTimeoutError) return true;
  return err instanceof TypeError;
}
